import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Droplets, 
  Palette, 
  Package, 
  Clock, 
  Award, 
  Users,
  Mail,
  Phone,
  MessageCircle,
  CheckCircle,
  ArrowRight,
  Sparkles,
  Wine
} from 'lucide-react';
import '../styles/modern-design.css';
import Header from './Header';
import Footer from './Footer';

const LandingPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    companyName: '',
    liquorTypes: [],
    bottleFormat: '',
    quantityRange: '',
    message: '',
    userType: 'business'
  });
  
  const liquorOptions = [
    { value: 'amaro', label: 'Amaro' },
    { value: 'gin', label: 'Gin' },
    { value: 'limoncello', label: 'Limoncello' },
    { value: 'bitter', label: 'Bitter' },
    { value: 'grappa', label: 'Grappa' },
    { value: 'liquore_erbe', label: 'Liquore alle Erbe' },
    { value: 'altro', label: 'Altro' }
  ];
  
  const formatOptions = [
    { value: '20cl', label: '20cl - Mignon' },
    { value: '50cl', label: '50cl - Standard' },
    { value: '70cl', label: '70cl - Premium' },
    { value: '100cl', label: '100cl - Magnum' }
  ];
  
  const quantityOptions = [
    { value: '24-120', label: '24 - 120 bottiglie' },
    { value: '120-240', label: '120 - 240 bottiglie' },
    { value: '240-480', label: '240 - 480 bottiglie' },
    { value: '480+', label: 'Oltre 480 bottiglie' }
  ];
  
  const handleLiquorToggle = (value) => {
    setFormData(prev => ({
      ...prev,
      liquorTypes: prev.liquorTypes.includes(value)
        ? prev.liquorTypes.filter(t => t !== value)
        : [...prev.liquorTypes, value]
    }));
  };
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          companyName: '',
          liquorTypes: [],
          bottleFormat: '',
          quantityRange: '',
          message: '',
          userType: 'business'
        });
        
        // Redirect alla home dopo 4 secondi
        setTimeout(() => {
          window.location.href = 'https://iltuoamaro.it';
        }, 4000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const features = [
    {
      icon: <Droplets className="w-8 h-8" />,
      title: "Liquori Personalizzati",
      description: "Crea il tuo liquore unico scegliendo tra una vasta selezione di ingredienti, botaniche e aromi selezionati."
    },
    {
      icon: <Palette className="w-8 h-8" />,
      title: "Design Personalizzato",
      description: "Progetta etichette personalizzate per bottiglie che rappresentano il tuo brand o evento speciale."
    },
    {
      icon: <Package className="w-8 h-8" />,
      title: "Produzione Artigianale",
      description: "Ogni bottiglia è realizzata con cura seguendo ricette uniche e processi artigianali di alta qualità."
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Consegna Rapida",
      description: "Spedizione in tutta Italia in 15 giorni lavorativi. Ordini superiori a 120 bottiglie con spedizione gratuita."
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: "Qualità Garantita",
      description: "Ingredienti di prima scelta e processi controllati per garantire l'eccellenza del prodotto finale."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Supporto Dedicato",
      description: "Team esperto pronto ad assisterti in ogni fase, dalla progettazione alla consegna finale."
    }
  ];

  const steps = [
    {
      number: "01",
      title: "Scegli gli Ingredienti",
      description: "Seleziona la base alcolica e le botaniche per creare la tua ricetta unica"
    },
    {
      number: "02",
      title: "Personalizza l'Etichetta",
      description: "Carica il tuo logo e progetta un'etichetta che rappresenta il tuo brand"
    },
    {
      number: "03",
      title: "Ricevi il Preventivo",
      description: "Ottieni un preventivo dettagliato con sconti per quantità crescenti"
    },
    {
      number: "04",
      title: "Produciamo e Spediamo",
      description: "Realizziamo il tuo ordine con cura artigianale e lo spediamo direttamente a te"
    }
  ];

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Hero Section - with Shimmer Gradient like Homepage */}
      <section className="hero-modern relative overflow-hidden text-white" style={{
        background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 25%, #2563eb 50%, #1d4ed8 75%, #1e3a8a 100%)',
        backgroundSize: '400% 400%',
        animation: 'gradient-animation 15s ease infinite'
      }}>
        {/* Shimmer overlay */}
        <div className="absolute inset-0 opacity-30" style={{
          background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%)',
          backgroundSize: '200% 200%',
          animation: 'gradient-animation 8s ease infinite'
        }}></div>
        
        {/* Floating decorative elements */}
        <div className="absolute top-1/4 left-10 floating opacity-20">
          <Wine className="w-16 h-16 text-white" />
        </div>
        <div className="absolute top-1/3 right-16 floating opacity-20" style={{animationDelay: '1s'}}>
          <Sparkles className="w-12 h-12 text-blue-200" />
        </div>
        <div className="absolute bottom-1/4 right-10 floating opacity-20" style={{animationDelay: '2s'}}>
          <Award className="w-14 h-14 text-white" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 z-10">
          <div className="text-center">
            {/* Logo */}
            <div className="mb-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_mixology-admin/artifacts/a22ywdhy_iltuoamaro-white%401000x.webp" 
                alt="Il Tuo Amaro Logo" 
                className="h-16 md:h-20 mx-auto"
              />
            </div>
            
            {/* Minimum Order Badge */}
            <div className="inline-flex items-center bg-white/20 backdrop-blur-sm rounded-full px-6 py-3 text-lg font-bold mb-8 border-2 border-white/40 shadow-lg">
              <Package className="w-6 h-6 mr-3 text-blue-200" />
              <span className="text-white">ORDINE MINIMO 24 BOTTIGLIE</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              <span className="text-white drop-shadow-lg">Private Label</span>
              <br />
              <span className="text-blue-200">Liquori Personalizzati</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-blue-100 max-w-3xl mx-auto">
              Crea liquori artigianali unici con il tuo marchio. Dalla ricetta all'etichetta, 
              realizziamo il prodotto perfetto per il tuo business con il nostro servizio Private Label.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#contatti"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-600 rounded-lg font-semibold text-lg hover:bg-blue-50 transition-all transform hover:scale-105 shadow-xl"
              >
                Richiedi Preventivo
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
              <a
                href="#contatti"
                className="inline-flex items-center justify-center px-8 py-4 bg-transparent border-2 border-white text-white rounded-lg font-semibold text-lg hover:bg-white hover:text-blue-600 transition-all"
              >
                Richiedi Info
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Tipi di Liquori Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold mb-4">
              🍾 La Nostra Offerta
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Tipologie di Liquori Disponibili
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Scegli tra le nostre specialità artigianali, ognuna personalizzabile al 100% per il tuo brand
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Amaro */}
            <div className="group relative bg-gradient-to-br from-amber-50 to-orange-100 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-amber-200">
              <div className="absolute top-4 right-4">
                <span className="text-4xl">🥃</span>
              </div>
              <div className="mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Droplets className="w-8 h-8 text-white" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Amaro</h3>
              <p className="text-gray-600 mb-4">
                Il classico digestivo italiano con erbe aromatiche, radici e spezie. Perfetto per ristoranti e hotel.
              </p>
              <ul className="text-sm text-gray-500 space-y-2">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Genziana, Rabarbaro, Carciofo
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Agrumi calabresi
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  25-35% vol.
                </li>
              </ul>
            </div>

            {/* Gin */}
            <div className="group relative bg-gradient-to-br from-blue-50 to-cyan-100 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-blue-200">
              <div className="absolute top-4 right-4">
                <span className="text-4xl">🍸</span>
              </div>
              <div className="mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Wine className="w-8 h-8 text-white" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Gin</h3>
              <p className="text-gray-600 mb-4">
                London Dry o Mediterranean style con botaniche selezionate. Ideale per bar e cocktail bar.
              </p>
              <ul className="text-sm text-gray-500 space-y-2">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Ginepro, Coriandolo, Angelica
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Botaniche mediterranee
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  40-45% vol.
                </li>
              </ul>
            </div>

            {/* Bitter */}
            <div className="group relative bg-gradient-to-br from-red-50 to-rose-100 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-red-200">
              <div className="absolute top-4 right-4">
                <span className="text-4xl">🍹</span>
              </div>
              <div className="mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-rose-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Sparkles className="w-8 h-8 text-white" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Bitter</h3>
              <p className="text-gray-600 mb-4">
                L'aperitivo per eccellenza con note agrumate e amaricanti. Perfetto per spritz e cocktail.
              </p>
              <ul className="text-sm text-gray-500 space-y-2">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  China, Arancia amara
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Erbe alpine
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  20-30% vol.
                </li>
              </ul>
            </div>

            {/* Limoncello & Altri */}
            <div className="group relative bg-gradient-to-br from-yellow-50 to-lime-100 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-yellow-200">
              <div className="absolute top-4 right-4">
                <span className="text-4xl">🍋</span>
              </div>
              <div className="mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-lime-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Palette className="w-8 h-8 text-white" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Limoncello & Altri</h3>
              <p className="text-gray-600 mb-4">
                Liquori alla frutta e cremosi. Dal Limoncello al Nocino, infinite possibilità.
              </p>
              <ul className="text-sm text-gray-500 space-y-2">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Limoni di Sorrento IGP
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Liquori cremosi
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  25-32% vol.
                </li>
              </ul>
            </div>
          </div>

          {/* CTA sotto i tipi di liquori */}
          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-6">
              Non trovi quello che cerchi? <strong>Contattaci</strong> per creare una ricetta completamente su misura!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#contatti"
                className="inline-flex items-center justify-center px-8 py-4 bg-blue-600 text-white rounded-lg font-semibold text-lg hover:bg-blue-700 transition-all transform hover:scale-105 shadow-lg"
              >
                Richiedi Preventivo Gratuito
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
              <a
                href="#contatti"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-600 border-2 border-blue-600 rounded-lg font-semibold text-lg hover:bg-blue-50 transition-all"
              >
                Richiedi Consulenza
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Crea la Tua Ricetta Section */}
      <section className="py-20 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-amber-100 text-amber-800 rounded-full text-sm font-semibold mb-4">
              🧪 Il Tuo Liquore, La Tua Scelta
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Crea la Tua Ricetta Perfetta
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Due strade per il tuo liquore personalizzato: scegli la semplicità delle nostre ricette 
              collaudate o lascia libera la tua creatività
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Opzione 1: Ricette Pronte */}
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-amber-400 to-orange-500 rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-300"></div>
              <div className="relative bg-white rounded-2xl p-8 shadow-xl h-full">
                <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <Award className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Ricette del Mastro Distillatore
                </h3>
                <p className="text-gray-600 mb-6">
                  Scegli tra le nostre <strong>ricette esclusive</strong>, create e perfettamente bilanciate 
                  dal nostro Mastro Distillatore con oltre 30 anni di esperienza. Ogni ricetta è stata 
                  sviluppata per garantire un profilo aromatico unico e un gusto impeccabile.
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Ricette collaudate e bilanciate</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Gusto garantito fin dal primo lotto</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Personalizzazione etichetta e bottiglia</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Tempi di produzione ridotti</span>
                  </li>
                </ul>
                <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                  <p className="text-sm text-amber-800 font-medium">
                    💡 Ideale per chi cerca qualità garantita e vuole concentrarsi sul branding
                  </p>
                </div>
              </div>
            </div>

            {/* Opzione 2: Ricetta Personalizzata */}
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-400 to-purple-500 rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-300"></div>
              <div className="relative bg-white rounded-2xl p-8 shadow-xl h-full">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-purple-500 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <Sparkles className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Componi la Tua Ricetta
                </h3>
                <p className="text-gray-600 mb-6">
                  Libera la tua creatività con il nostro <strong>configuratore avanzato</strong>. 
                  Scegli tra oltre <strong className="text-purple-600">60 botanicals</strong>, spezie, 
                  erbe aromatiche e ingredienti pregiati per creare un liquore unico che rispecchi 
                  la tua visione.
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700"><strong>60+ botanicals</strong> selezionati</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Assistenza del Mastro Distillatore</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Campione di prova prima della produzione</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Ricetta esclusiva e riservata a te</span>
                  </li>
                </ul>
                <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                  <p className="text-sm text-purple-800 font-medium">
                    🎨 Ideale per chi vuole un prodotto unico e distintivo sul mercato
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Botanicals Preview */}
          <div className="mt-16 text-center">
            <p className="text-gray-500 mb-6 text-sm uppercase tracking-wider font-semibold">
              Alcuni dei nostri botanicals premium
            </p>
            <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
              {['Ginepro', 'Coriandolo', 'Angelica', 'Liquirizia', 'Cardamomo', 'Bergamotto', 
                'Cannella', 'Chiodi di Garofano', 'Scorza di Arancia', 'Iris', 'Rabarbaro', 
                'Genziana', 'Assenzio', 'Finocchio', 'Anice Stellato', 'Zenzero'].map((botanical, index) => (
                <span 
                  key={index}
                  className="px-4 py-2 bg-white rounded-full text-sm text-gray-700 shadow-sm border border-gray-100 hover:border-amber-300 hover:shadow-md transition-all"
                >
                  {botanical}
                </span>
              ))}
              <span className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full text-sm text-white font-semibold shadow-md">
                +44 altri...
              </span>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-12 text-center">
            <a
              href="#contatti"
              className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-semibold text-lg hover:from-amber-600 hover:to-orange-600 transition-all transform hover:scale-105 shadow-lg"
            >
              <Wine className="w-5 h-5 mr-2" />
              Richiedi il Tuo Preventivo
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </div>
        </div>
      </section>

      {/* Migliore Distilleria Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold mb-4">
                🏆 Eccellenza Italiana
              </div>
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
                La Migliore Distilleria per il Tuo Private Label
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Con oltre 20 anni di esperienza nella produzione di liquori artigianali, 
                siamo il partner ideale per creare il tuo brand di liquori personalizzati.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Award className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Qualità Certificata</h3>
                    <p className="text-gray-600">Produzione secondo i più alti standard qualitativi italiani ed europei</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Droplets className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Ingredienti Selezionati</h3>
                    <p className="text-gray-600">Solo materie prime di prima scelta provenienti da fornitori certificati</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Team Esperto</h3>
                    <p className="text-gray-600">Mastri distillatori con decenni di esperienza nel settore</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-2xl bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
                <div className="text-center p-8">
                  <p className="text-6xl font-bold text-blue-600 mb-2">20+</p>
                  <p className="text-xl text-gray-700 font-semibold">Anni di Esperienza</p>
                  <div className="mt-8 grid grid-cols-2 gap-4 text-center">
                    <div className="bg-white rounded-lg p-4">
                      <p className="text-3xl font-bold text-blue-600">500+</p>
                      <p className="text-sm text-gray-600">Progetti Realizzati</p>
                    </div>
                    <div className="bg-white rounded-lg p-4">
                      <p className="text-3xl font-bold text-blue-600">98%</p>
                      <p className="text-sm text-gray-600">Clienti Soddisfatti</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Perché Scegliere Noi
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              La soluzione completa per creare liquori personalizzati di alta qualità
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="p-6 rounded-xl bg-gradient-to-br from-blue-50 to-white border border-blue-100 hover:shadow-xl transition-all transform hover:-translate-y-1"
              >
                <div className="w-16 h-16 bg-blue-600 rounded-lg flex items-center justify-center text-white mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Come Funziona
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Quattro semplici passi per il tuo liquore personalizzato
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="text-center">
                  <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4 shadow-lg">
                    {step.number}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-gray-600">
                    {step.description}
                  </p>
                </div>
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-10 left-full w-full h-0.5 bg-blue-200 -ml-4"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Ideale per il Tuo Business
              </h2>
              <div className="space-y-4">
                {[
                  "Ristoranti e Hotel di lusso",
                  "Eventi aziendali e matrimoni",
                  "Negozi e rivenditori specializzati",
                  "Produttori di liquori artigianali",
                  "Regali corporate personalizzati"
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                    <span className="text-lg text-gray-700">{item}</span>
                  </div>
                ))}
              </div>

              <div className="mt-8 p-6 bg-blue-50 rounded-xl border border-blue-200">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  💰 Sconti per Quantità
                </h3>
                <p className="text-gray-700">
                  Più ordini, più risparmi! Sconti fino al 30% per ordini superiori a 421 bottiglie.
                  Spedizione gratuita per ordini superiori a 120 bottiglie.
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl p-8 text-white shadow-2xl">
              <h3 className="text-2xl font-bold mb-6">Vantaggi Esclusivi</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 flex-shrink-0 mt-1" />
                  <div>
                    <strong>Ordine minimo:</strong> Solo 24 bottiglie per iniziare
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 flex-shrink-0 mt-1" />
                  <div>
                    <strong>Consegna rapida:</strong> 15 giorni lavorativi garantiti
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 flex-shrink-0 mt-1" />
                  <div>
                    <strong>Supporto completo:</strong> Ti seguiamo in ogni fase del processo
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 flex-shrink-0 mt-1" />
                  <div>
                    <strong>Pagamento flessibile:</strong> 50% acconto, saldo alla consegna
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Progetti Realizzati Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold mb-4">
              🎯 Portfolio
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              I Nostri Progetti Private Label
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Alcuni dei brand che hanno scelto la nostra distilleria per i loro liquori personalizzati
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Progetto 1 - Amaro di Stabia */}
            <div className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2">
              <div className="aspect-square">
                <img 
                  src="https://customer-assets.emergentagent.com/job_landing-fix-2/artifacts/3ppo5kjd_WhatsApp%20Image%202025-12-11%20at%2016.54.48.jpeg"
                  alt="Amaro di Stabia - Spirito della Costa"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h3 className="font-bold text-xl mb-1">Amaro di Stabia</h3>
                  <p className="text-sm text-blue-200">Spirito della Costa</p>
                </div>
              </div>
            </div>

            {/* Progetto 2 - Amaro Agatino */}
            <div className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2">
              <div className="aspect-square">
                <img 
                  src="https://customer-assets.emergentagent.com/job_landing-fix-2/artifacts/1xze9xcg_WhatsApp%20Image%202025-12-11%20at%2016.58.16.jpeg"
                  alt="Amaro Agatino - Premium Liqueur"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h3 className="font-bold text-xl mb-1">Amaro Agatino</h3>
                  <p className="text-sm text-blue-200">Premium Liqueur</p>
                </div>
              </div>
            </div>

            {/* Progetto 3 - Limoncello dei Lupi */}
            <div className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2">
              <div className="aspect-square">
                <img 
                  src="https://customer-assets.emergentagent.com/job_landing-fix-2/artifacts/e8pumadt_WhatsApp%20Image%202025-12-11%20at%2016.51.21.jpeg"
                  alt="Limoncello dei Lupi - L'anima selvaggia del Molise"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h3 className="font-bold text-xl mb-1">Limoncello dei Lupi</h3>
                  <p className="text-sm text-yellow-200">L'anima selvaggia del Molise</p>
                </div>
              </div>
            </div>

            {/* Progetto 4 - Amaro Gustosiia */}
            <div className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2">
              <div className="aspect-square">
                <img 
                  src="https://customer-assets.emergentagent.com/job_landing-fix-2/artifacts/ukjqohh8_WhatsApp%20Image%202025-12-11%20at%2016.53.07.jpeg"
                  alt="Amaro Gustosiia - Anima Amara Silana"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h3 className="font-bold text-xl mb-1">Amaro Gustosiia</h3>
                  <p className="text-sm text-amber-200">Anima Amara Silana - Gift Set</p>
                </div>
              </div>
            </div>

            {/* Progetto 5 - Amaro U Ciaciu */}
            <div className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2">
              <div className="aspect-square">
                <img 
                  src="https://customer-assets.emergentagent.com/job_spirits-builder/artifacts/k032z0p5_WhatsApp%20Image%202025-12-16%20at%2017.47.08.jpeg"
                  alt="Amaro U Ciaciu - Amaro di Famiglia"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h3 className="font-bold text-xl mb-1">Amaro U Ciaciu</h3>
                  <p className="text-sm text-blue-200">Amaro di Famiglia</p>
                </div>
              </div>
            </div>

            {/* Card CTA - Il Tuo Progetto */}
            <div className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2 bg-gradient-to-br from-blue-600 to-blue-800">
              <div className="aspect-square flex items-center justify-center p-8">
                <div className="text-center text-white">
                  <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Sparkles className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="font-bold text-2xl mb-2">Il Tuo Brand Qui?</h3>
                  <p className="text-blue-100 mb-6">
                    Crea il tuo liquore personalizzato e unisciti ai nostri partner
                  </p>
                  <a
                    href="#contatti"
                    className="inline-flex items-center px-6 py-3 bg-white text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
                  >
                    Inizia Ora
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-4">
              <strong>+500 progetti</strong> realizzati per ristoranti, hotel, bar e aziende in tutta Italia
            </p>
          </div>
        </div>
      </section>

      {/* Recensioni Section */}
      <section className="py-20 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Cosa Dicono i Nostri Clienti
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              La soddisfazione dei nostri partner è la nostra priorità
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: "Marco Rossi",
                company: "Ristorante Bella Vista",
                rating: 5,
                text: "Abbiamo creato il nostro liquore digestivo personalizzato e i clienti ne sono entusiasti. Qualità eccellente e servizio impeccabile!"
              },
              {
                name: "Laura Bianchi",
                company: "Hotel Luxury SPA",
                rating: 5,
                text: "Il servizio Private Label ci ha permesso di offrire un prodotto esclusivo ai nostri ospiti. Professionalità e tempistiche rispettate."
              },
              {
                name: "Giuseppe Verdi",
                company: "Enoteca Il Sommelier",
                rating: 5,
                text: "Collaboriamo da anni per la nostra linea di liquori artigianali. Sempre disponibili e attenti alle nostre esigenze. Consigliati!"
              }
            ].map((review, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(review.rating)].map((_, i) => (
                    <span key={i} className="text-yellow-400 text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{review.text}"</p>
                <div className="border-t pt-4">
                  <p className="font-bold text-gray-900">{review.name}</p>
                  <p className="text-sm text-gray-600">{review.company}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center p-8 bg-white rounded-2xl shadow-lg">
            <div className="flex items-center justify-center gap-2 mb-4">
              <span className="text-5xl font-bold text-blue-600">4.9</span>
              <div className="text-left">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-yellow-400 text-xl">★</span>
                  ))}
                </div>
                <p className="text-sm text-gray-600">Media recensioni</p>
              </div>
            </div>
            <p className="text-gray-600">Oltre 200 recensioni positive dai nostri clienti</p>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contatti" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Richiedi Informazioni
            </h2>
            <p className="text-xl text-gray-600">
              Compila il form e ti ricontatteremo entro 24 ore
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nome e Cognome *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Mario Rossi"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="mario@esempio.it"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Telefono
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="+39 345 1234567"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Azienda
                  </label>
                  <input
                    type="text"
                    value={formData.companyName}
                    onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nome Azienda"
                  />
                </div>
              </div>

              {/* Liquori di interesse */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  🍾 Liquori di interesse
                </label>
                <div className="flex flex-wrap gap-2">
                  {liquorOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleLiquorToggle(option.value)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        formData.liquorTypes.includes(option.value)
                          ? 'bg-blue-600 text-white shadow-md'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {formData.liquorTypes.includes(option.value) && '✓ '}
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Formato bottiglia */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    📏 Formato bottiglia
                  </label>
                  <select
                    value={formData.bottleFormat}
                    onChange={(e) => setFormData({ ...formData, bottleFormat: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  >
                    <option value="">Seleziona formato...</option>
                    {formatOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Quantità */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    📦 Quantità richiesta
                  </label>
                  <select
                    value={formData.quantityRange}
                    onChange={(e) => setFormData({ ...formData, quantityRange: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  >
                    <option value="">Seleziona quantità...</option>
                    {quantityOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Messaggio *
                </label>
                <textarea
                  required
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows="4"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Raccontaci del tuo progetto... Hai esigenze particolari? Personalizzazioni specifiche?"
                ></textarea>
              </div>

              {submitStatus === 'success' && (
                <div className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl shadow-lg">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-green-800 mb-2">
                        🎉 Grazie per averci contattato!
                      </h3>
                      <p className="text-green-700 mb-3">
                        Abbiamo ricevuto la tua richiesta e un nostro consulente ti ricontatterà entro <strong>24 ore</strong>.
                      </p>
                      <p className="text-sm text-green-600">
                        Stai per essere reindirizzato alla homepage...
                      </p>
                      <div className="mt-3 flex items-center gap-2">
                        <div className="w-full bg-green-200 rounded-full h-2">
                          <div className="bg-green-500 h-2 rounded-full animate-pulse" style={{width: '100%', animation: 'shrink 4s linear forwards'}}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800">
                    Si è verificato un errore. Riprova o contattaci direttamente.
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 text-white py-4 rounded-lg font-semibold text-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>Invio in corso...</>
                ) : (
                  <>
                    <Mail className="w-5 h-5" />
                    Invia Richiesta
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="mt-12 grid md:grid-cols-3 gap-6 text-center">
            <div className="p-6 bg-white rounded-lg shadow-md">
              <Mail className="w-8 h-8 text-blue-600 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
              <a href="mailto:preventivi@iltuoamaro.it" className="text-blue-600 hover:text-blue-800">
                preventivi@iltuoamaro.it
              </a>
            </div>
            <div className="p-6 bg-white rounded-lg shadow-md">
              <Phone className="w-8 h-8 text-blue-600 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900 mb-1">Telefono</h3>
              <a href="tel:+393939587340" className="text-blue-600 hover:text-blue-800">
                +39 393 9587340
              </a>
            </div>
            <div className="p-6 bg-white rounded-lg shadow-md">
              <MessageCircle className="w-8 h-8 text-blue-600 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900 mb-1">Assistenza</h3>
              <p className="text-gray-600">Lun-Ven 9:00-18:00</p>
            </div>
          </div>
        </div>
      </section>
    </div>
    <Footer />
    </>
  );
};

export default LandingPage;
